import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const MobileAuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Storage keys
const STORAGE_KEYS = {
  token: 'mobile_token',
  operator: 'mobile_operator',
  deviceToken: 'mobile_device_token',
  authType: 'mobile_auth_type',
  trustedUserInfo: 'mobile_trusted_user'
};

export const MobileAuthProvider = ({ children }) => {
  const [operator, setOperator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem(STORAGE_KEYS.token));
  
  // V2 Auth state
  const [authType, setAuthType] = useState(localStorage.getItem(STORAGE_KEYS.authType) || 'legacy');
  const [deviceToken, setDeviceToken] = useState(localStorage.getItem(STORAGE_KEYS.deviceToken));
  const [trustedUserInfo, setTrustedUserInfo] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.trustedUserInfo);
    return stored ? JSON.parse(stored) : null;
  });
  const [v2AuthEnabled, setV2AuthEnabled] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [pendingPinSetup, setPendingPinSetup] = useState(false);
  const [pendingPasswordChange, setPendingPasswordChange] = useState(false);
  const [pendingOperatorData, setPendingOperatorData] = useState(null); // Store operator data before password change

  const api = axios.create({
    baseURL: API_URL,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  // Check if v2 auth is enabled
  useEffect(() => {
    const checkAuthFlags = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/auth/feature-flags`);
        setV2AuthEnabled(response.data.new_auth_enabled || false);
      } catch {
        setV2AuthEnabled(false);
      }
    };
    checkAuthFlags();
  }, []);

  // Initialize from storage
  useEffect(() => {
    const storedOperator = localStorage.getItem(STORAGE_KEYS.operator);
    if (storedOperator && token) {
      setOperator(JSON.parse(storedOperator));
    }
    setLoading(false);
  }, [token]);

  // Update api headers when token changes
  useEffect(() => {
    if (token) {
      api.defaults.headers.Authorization = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.Authorization;
    }
  }, [token, api]);

  // Check if device token is still valid
  const checkDeviceToken = useCallback(async () => {
    if (!deviceToken) return { valid: false, reason: 'no_token' };
    
    try {
      const response = await axios.get(
        `${API_URL}/api/v2/mobile/auth/check-device?device_token=${deviceToken}`
      );
      
      if (response.data.valid) {
        // Update stored user info
        const userInfo = {
          name: response.data.user_name,
          email: response.data.user_email,
          deviceName: response.data.device_name,
          trustedAt: response.data.trusted_at
        };
        setTrustedUserInfo(userInfo);
        localStorage.setItem(STORAGE_KEYS.trustedUserInfo, JSON.stringify(userInfo));
        return { valid: true, userInfo };
      }
      
      // Device token invalid - clear it
      clearDeviceToken();
      return { valid: false, reason: response.data.reason };
    } catch (error) {
      console.error('Device check failed:', error);
      return { valid: false, reason: 'error' };
    }
  }, [deviceToken]);

  // Legacy login (Client ID + Operator Code + PIN)
  const login = async (clientId, operatorCode, pin) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/mobile/auth/login`,
        { client_id: clientId, operator_code: operatorCode, pin }
      );
      
      const { access_token, operator: opData } = response.data;
      
      localStorage.setItem(STORAGE_KEYS.token, access_token);
      localStorage.setItem(STORAGE_KEYS.operator, JSON.stringify(opData));
      localStorage.setItem(STORAGE_KEYS.authType, 'legacy');
      
      setToken(access_token);
      setOperator(opData);
      setAuthType('legacy');
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  // V2 Email + Password login
  const loginV2 = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/v2/mobile/auth/login`, {
        email,
        password
      });
      
      const { access_token, user, tenants: userTenants, requires_tenant_selection, requires_pin_setup, requires_password_change } = response.data;
      
      localStorage.setItem(STORAGE_KEYS.token, access_token);
      localStorage.setItem(STORAGE_KEYS.authType, 'v2');
      
      // Store operator-like data from user
      const opData = {
        id: user.id,
        name: user.full_name,
        email: user.email,
        client_id: user.active_tenant,
        // Will be populated after tenant selection if needed
        tenant_features: [],
        mobile_quotes_enabled: false
      };
      
      setToken(access_token);
      setAuthType('v2');
      setTenants(userTenants || []);
      
      // If password change is required, DON'T set operator yet
      // This prevents the redirect to dashboard
      if (requires_password_change) {
        setPendingOperatorData(opData);
        setPendingPasswordChange(true);
        // Don't store operator in localStorage yet
      } else {
        localStorage.setItem(STORAGE_KEYS.operator, JSON.stringify(opData));
        setOperator(opData);
        setPendingPinSetup(requires_pin_setup);
      }
      
      return { 
        success: true, 
        requiresTenantSelection: requires_tenant_selection,
        requiresPinSetup: requires_pin_setup && !requires_password_change,
        requiresPasswordChange: requires_password_change,
        tenants: userTenants,
        user
      };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  // Change password (for must_change_password flow)
  const changePassword = async (currentPassword, newPassword) => {
    try {
      await axios.post(
        `${API_URL}/api/v2/mobile/auth/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Clear the pending password change flag
      setPendingPasswordChange(false);
      
      // Now set the operator data (this will trigger navigation)
      if (pendingOperatorData) {
        localStorage.setItem(STORAGE_KEYS.operator, JSON.stringify(pendingOperatorData));
        setOperator(pendingOperatorData);
        setPendingOperatorData(null);
      }
      
      // Now they should set up PIN
      setPendingPinSetup(true);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Failed to change password' 
      };
    }
  };

  // V2 PIN login (for trusted devices)
  const loginWithPin = async (pin) => {
    if (!deviceToken) {
      return { success: false, error: 'No device token. Please log in with email.' };
    }
    
    try {
      const response = await axios.post(`${API_URL}/api/v2/mobile/auth/pin-login`, {
        device_token: deviceToken,
        pin
      });
      
      const { access_token, user } = response.data;
      
      localStorage.setItem(STORAGE_KEYS.token, access_token);
      localStorage.setItem(STORAGE_KEYS.authType, 'v2');
      
      const opData = {
        id: user.id,
        name: user.full_name,
        email: user.email,
        client_id: user.active_tenant,
        company_name: user.company_name,
        role: user.role,
        tenant_features: [],
        mobile_quotes_enabled: false
      };
      
      localStorage.setItem(STORAGE_KEYS.operator, JSON.stringify(opData));
      
      setToken(access_token);
      setOperator(opData);
      setAuthType('v2');
      
      return { success: true };
    } catch (error) {
      const detail = error.response?.data?.detail || 'PIN login failed';
      return { 
        success: false, 
        error: detail,
        // Check if it's a lockout
        isLocked: detail.includes('Too many failed attempts'),
        // Check if device is no longer recognized
        deviceInvalid: detail.includes('Device not recognized')
      };
    }
  };

  // Setup PIN for device trust
  const setupPin = async (pin, deviceName = null, deviceType = 'mobile') => {
    if (!token) {
      return { success: false, error: 'Not authenticated' };
    }
    
    try {
      const response = await axios.post(
        `${API_URL}/api/v2/mobile/auth/setup-pin`,
        { pin, device_name: deviceName, device_type: deviceType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { device_token: newDeviceToken } = response.data;
      
      // Store device token
      localStorage.setItem(STORAGE_KEYS.deviceToken, newDeviceToken);
      setDeviceToken(newDeviceToken);
      
      // Store trusted user info
      const userInfo = {
        name: operator?.name,
        email: operator?.email,
        trustedAt: new Date().toISOString()
      };
      localStorage.setItem(STORAGE_KEYS.trustedUserInfo, JSON.stringify(userInfo));
      setTrustedUserInfo(userInfo);
      
      setPendingPinSetup(false);
      
      return { success: true, deviceToken: newDeviceToken };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Failed to setup PIN' 
      };
    }
  };

  // Switch account (clear device trust)
  const switchAccount = async () => {
    if (deviceToken) {
      try {
        await axios.post(`${API_URL}/api/v2/mobile/auth/switch-account`, {
          device_token: deviceToken
        });
      } catch (error) {
        console.error('Failed to clear device trust on server:', error);
      }
    }
    
    // Clear local device token
    clearDeviceToken();
    
    // Also logout
    logout();
    
    return { success: true };
  };

  // Clear device token only (keep session if active)
  const clearDeviceToken = () => {
    localStorage.removeItem(STORAGE_KEYS.deviceToken);
    localStorage.removeItem(STORAGE_KEYS.trustedUserInfo);
    setDeviceToken(null);
    setTrustedUserInfo(null);
  };

  // Logout
  const logout = () => {
    localStorage.removeItem(STORAGE_KEYS.token);
    localStorage.removeItem(STORAGE_KEYS.operator);
    localStorage.removeItem(STORAGE_KEYS.authType);
    // Note: We keep device token on logout so user can PIN login again
    setToken(null);
    setOperator(null);
    setAuthType('legacy');
    setTenants([]);
    setPendingPinSetup(false);
  };

  // Check if a feature is available based on tenant's plan
  const hasFeature = useCallback((feature) => {
    if (!operator?.tenant_features) return false;
    return operator.tenant_features.includes(feature);
  }, [operator]);

  // Check if operator has mobile quotes access
  const hasMobileQuotesAccess = useCallback(() => {
    return operator?.tenant_features?.includes('quotes') && operator?.mobile_quotes_enabled === true;
  }, [operator]);

  return (
    <MobileAuthContext.Provider value={{ 
      // State
      operator, 
      loading, 
      token,
      authType,
      deviceToken,
      trustedUserInfo,
      v2AuthEnabled,
      tenants,
      pendingPinSetup,
      pendingPasswordChange,
      
      // API
      api,
      
      // Legacy auth
      login,
      
      // V2 auth
      loginV2,
      loginWithPin,
      setupPin,
      switchAccount,
      checkDeviceToken,
      clearDeviceToken,
      changePassword,
      
      // Common
      logout,
      hasFeature,
      hasMobileQuotesAccess
    }}>
      {children}
    </MobileAuthContext.Provider>
  );
};

export const useMobileAuth = () => {
  const context = useContext(MobileAuthContext);
  if (!context) {
    throw new Error('useMobileAuth must be used within MobileAuthProvider');
  }
  return context;
};
